<template>
  <div class="chart-config">
    <div class="chart-config__section-label font-section mb-16">
      SETTINGS
    </div>

    <div
      v-if="databases.length > 0"
      class="chart-config__dataset mb-16">
      <label class="font-label">Dataset</label>
      <div class="relative">
        <SelectDropdown
          :inputOptions="databasesInputOptions"
          :inputValue="currentDatabaseId"
          @selected="updateDatabase" />
      </div>
    </div>

    <div class="chart-config__type mb-16">
      <label class="font-label">Type</label>
      <div class="relative">
        <SelectDropdown
          :inputOptions="chartTypesInputOptions"
          :inputValue="chartType"
          @selected="updateType" />
      </div>
    </div>

    <div
      v-if="chartType !== 'table' && fields.length > 0"
      class="chart-config__fields mb-16">
      <div class="chart-config__config-row mb-12">
        <div class="chart-config__config-header-cell chart-config__config-header-cell--wide font-label">
          Column
        </div>
        <div class="chart-config__config-header-cell font-label">
          X-axis
        </div>
        <div class="chart-config__config-header-cell font-label">
          Y-axis
        </div>
        <div class="chart-config__config-header-cell font-label">
          Series
        </div>
      </div>

      <div
        :key="`${field}-${index}`"
        v-for="(field, index) in fields"
        class="chart-config__config-row mb-8">
        <div class="chart-config__config-cell font-small">
          {{ field }}
        </div>
        <div class="chart-config__config-cell">
          <RadioButton
            name="x-axis"
            :value="field"
            :checked="xAxis === field"
            @input="updateXAxis" />
        </div>
        <div class="chart-config__config-cell">
          <Checkbox
            :value="field"
            :disabled="xAxis === field"
            :checked="yAxis.includes(field)"
            @input="updateYAxis" />
        </div>
        <div class="chart-config__config-cell">
          <Checkbox
            :value="field"
            :disabled="xAxis === field"
            :checked="seriesColumns.includes(field)"
            @input="updateSeries" />
        </div>
      </div>
    </div>

    <div
      v-if="chartType === 'table' && fields.length > 0"
      class="chart-config__fields mb-16">
      <div class="chart-config__config-row mb-12">
        <div class="chart-config__config-header-cell chart-config__config-header-cell--wide font-label">
          Column
        </div>
        <div class="chart-config__config-header-cell font-label">
          Included
        </div>
      </div>

      <div
        :key="`${field}-${index}`"
        v-for="(field, index) in fields"
        class="chart-config__config-row mb-8">
        <div class="chart-config__config-cell font-small">
          {{ field }}
        </div>
        <div class="chart-config__config-cell">
          <Checkbox
            :value="field"
            :checked="tableColumns.includes(field)"
            @input="updateTableColumns" />
        </div>
      </div>
    </div>

    <div
      v-if="chartType === 'bar'"
      class="chart-config__checkbox-option flex items-center mb-16">
      <Checkbox
        value="stacked"
        label="Stacked"
        :checked="stacked"
        @input="updateStacked" />
    </div>

    <div
      v-if="chartType === 'bar'"
      class="chart-config__checkbox-option flex items-center mb-16">
      <Checkbox
        value="showAllXAxisLabels"
        label="Show all X-axis labels"
        :checked="showAllXAxisLabels"
        @input="updateShowAllXAxisLabels" />
    </div>

    <div class="chart-config__title mb-16">
      <label class="font-label">Title</label>
      <TextInput
        cssClass="font-small"
        :value="title"
        @input="updateChartTitle" />
    </div>

    <div
      class="chart-config__x-axis-name mb-16"
      v-if="['line', 'bar'].includes(chartType)">
      <label class="font-label">X-axis name</label>
      <TextInput
        cssClass="font-small"
        @input="updateXAxisTitle" />
    </div>

    <div
      class="chart-config__y-axis-name mb-16"
      v-if="['line', 'bar'].includes(chartType)">
      <label class="font-label">Y-axis name</label>
      <TextInput
        cssClass="font-small"
        @input="updateYAxisTitle" />
    </div>

    <div
      v-if="chartType !== 'table'"
      class="chart-config__type">
      <label class="font-label">Legend position</label>

      <div class="relative">
        <SelectDropdown
          :inputOptions="legendPositionInputOptions"
          :inputValue="legendPosition"
          @selected="updateLegendPosition" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import SelectDropdown from "components/select-dropdown";
import TextInput from "components/shared/text-input";
import Checkbox from "components/shared/checkbox";
import RadioButton from "components/shared/radio-button";

export default {
  components: {
    SelectDropdown,
    TextInput,
    Checkbox,
    RadioButton
  },
  computed: {
    ...mapGetters("chart", ["title", "xAxis", "yAxis", "series", "seriesColumns", "xAxisTitle", "yAxisTitle", "chartType", "chartTypes", "fields", "stacked", "legendPosition", "legendPositions", "databases", "currentDatabaseId", "showAllXAxisLabels", "tableColumns"]),
    chartTypesInputOptions() {
      return this.chartTypes.map(typeKey => {
        return [
          this.$i18n.t(`chartType.${typeKey}`),
          typeKey,
          this.$i18n.t(`chartTypeIcon.${typeKey}`)
        ];
      });
    },
    legendPositionInputOptions() {
      return this.legendPositions.map(positionKey => {
        return [this.$i18n.t(`legendPosition.${positionKey}`), positionKey];
      });
    },
    databasesInputOptions() {
      return this.databases.map(d => {
        return [
          d.name,
          d.id,
          "font_awesome/database-light.svg"
        ];
      });
    }
  },
  methods: {
    updateType(newType) {
      this.$store.dispatch("chart/setChartType", newType);
    },
    updateChartTitle(newTitle) {
      this.$store.dispatch("chart/setChartTitle", newTitle);
    },
    updateXAxisTitle(newTitle) {
      this.$store.dispatch("chart/setXAxisTitle", newTitle);
    },
    updateYAxisTitle(newTitle) {
      this.$store.dispatch("chart/setYAxisTitle", newTitle);
    },
    updateXAxis(value, checked) {
      this.$store.dispatch("chart/setXAxis", {
        value: value,
        checked: checked
      });
    },
    updateYAxis(value, checked) {
      this.$store.dispatch("chart/setYAxis", {
        value: value,
        checked: checked
      });
    },
    updateSeries(value, checked) {
      this.$store.dispatch("chart/setSeries", {
        value: value,
        checked: checked
      });
    },
    updateTableColumns(value, checked) {
      this.$store.dispatch("chart/setTableColumns", {
        value: value,
        checked: checked
      });
    },
    updateStacked(value, checked) {
      this.$store.dispatch("chart/setStacked", checked);
    },
    updateLegendPosition(newPosition) {
      this.$store.dispatch("chart/setLegendPosition", newPosition);
    },
    updateDatabase(databaseId) {
      this.$store.dispatch("chart/setCurrentDatabaseId", databaseId);
    },
    updateShowAllXAxisLabels(value, checked) {
      this.$store.dispatch("chart/setShowAllXAxisLabels", checked);
    }
  },
  i18n: {
    messages: {
      en: {
        chartType: {
          line: "Line",
          bar: "Bar",
          pie: "Pie",
          table: "Table"
        },
        chartTypeIcon: {
          line: "font_awesome/chart-line-light.svg",
          bar: "font_awesome/chart-bar-light.svg",
          pie: "font_awesome/chart-pie-light.svg",
          table: "font_awesome/table-light.svg",
        },
        legendPosition: {
          top: "Top",
          left: "Left",
          bottom: "Bottom",
          right: "Right"
        }
      }
    }
  }
}
</script>

<style lang="scss">
.chart-config {
  width: 100%;

  &__type {
    position: relative;
  }

  &__config-row {
    display: flex;
  }

  &__config-header-cell {
    flex-grow: 1;

    &:first-child {
      width: 45%;
      max-width: 45%;
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    &:not(:first-child) {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &__config-cell {
    flex-grow: 1;

    &:first-child {
      width: 45%;
      max-width: 45%;
      display: flex;
      align-items: center;
      justify-content: flex-start;
    }

    &:not(:first-child) {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }

  &__link-back-icon {
    display: block;
    width: $px16;
    height: $px16;
    margin-right: $px8;

    color: $grey-1;
  }

  &__link-back {
    display: flex;
    align-items: center;

    @include font-small;
    color: $grey-1;
    text-decoration: none;
  }
}
</style>
